import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Table, Form, Button, Card, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';
import { FaTrash, FaSearch, FaPlus, FaPencilAlt, FaCopy } from 'react-icons/fa';
import { fetchProductList, deleteProduct } from '../../store/actions';
import defaultProductImage from '../../images/default-product.png';
import useTitle from '../../hooks/useTitle';
import { showNotification } from '../../store/actions';
import productService from '../../services/productService';
import './AdminProducts.css';

const AdminProducts = () => {
    const { data: { items: products, totalItems: totalProducts, currentPage, totalPages }, loading: productsLoading, error: productsError } = useSelector(state => state.productList);
    const { data: categories } = useSelector(state => state.categories.all);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchTimeoutRef = useRef(null);

    // Состояние фильтров и поиска
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPageState, setCurrentPageState] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    // Задаем название сайта
    useTitle('Продукция | Админ-панель');
    
    // Загрузка продуктов с фильтрами
    useEffect(() => {
        fetchProducts();
    }, [currentPageState, pageSize, selectedCategory, dispatch]);

    const fetchProducts = (query = null) => {
        let params = {
            currentPage: currentPageState,
            pageSize: pageSize
        };
        
        if (query !== null) {
            // Добавляем параметр q только если строка не пустая
            if (query.trim()) {
                params.q = query.trim();
            }
        }
        // Иначе используем текущее состояние searchQuery
        else if (searchQuery.trim()) {
            if (searchQuery.trim()) {
                params.q = searchQuery.trim();
            }
        }
        
        if (selectedCategory) {
            params.categories = selectedCategory;
        }
        dispatch(fetchProductList(params));
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setCurrentPageState(1); // Сброс на первую страницу при смене категории
    };

    const handleSearchQueryChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Debounce для предотвращения слишком частых запросов
        // Создаем локальную переменную для хранения таймера
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchProducts(query);
            setCurrentPageState(1); // Сброс на первую страницу при смене категории
        }, 500);
    }

    useEffect(() => {
        // Очистка таймера при размонтировании
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handlePageChange = (pageNumber) => {
        setCurrentPageState(pageNumber);
    };

    const handleEditProduct = (productId) => {
        navigate(`/performance-admin-panel/products/${productId}`);
    };

    const handleDuplicateProduct = async (productId) => { 
        // Логика дублирования продукта
        const result = await productService.duplicateProduct(productId);
        if (result.success) {
            dispatch(showNotification('Копия товара успешно создана', 'success'));
            // Обновление списка товаров после дублирования
            handleEditProduct(result.data.data.id);
        }
        else {
            dispatch(showNotification(result.error, 'error'));
        }
    };

    const handleAddProduct = () => {
        navigate('/performance-admin-panel/products/new');
    };

    const openDeleteModal = (product) => {
        setProductToDelete(product);
        setShowDeleteModal(true);
    };

    const handleDeleteProduct = async () => {
        if (productToDelete) {
            setShowDeleteModal(false);
            const result = await dispatch(deleteProduct(productToDelete.id));
            if (result.success) {
                // Обновление списка товаров после удаления
                dispatch(fetchProductList({
                    currentPage: currentPageState,
                    pageSize: pageSize,
                    q: searchQuery,
                    categories: selectedCategory
                }));
            }
        }
    };

    // Функция форматирования цены
    const formatPrice = (price) => {
        return price !== null ? `${price} ₽` : "По запросу";
    };

    return (
        <Container className="py-4">
            <Row className="mb-4 align-items-center">
                <Col>
                    <h2>Продукция</h2>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="primary" 
                        onClick={handleAddProduct}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-1" /> Добавить
                    </Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6} lg={4} className="mb-2 mb-md-0">
                            <Form.Group className="position-relative">
                                <Form.Control
                                    placeholder="Поиск товаров..."
                                    value={searchQuery}
                                    onChange={handleSearchQueryChange}
                                    style={{ paddingLeft: '2.5rem' }}
                                />
                                <div className="position-absolute" style={{ top: '0', bottom: '0', left: '15px', display: 'flex', alignItems: 'center' }}>
                                    <FaSearch className="text-muted" />
                                </div>
                                <Button type="submit" className="d-none">Поиск</Button>
                            </Form.Group>
                        </Col>
                        <Col md={6} lg={4}>
                            <Form.Select
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                            >
                                <option value="">Все категории</option>
                                {categories && categories.map(category => (
                                    <option key={category.id} value={category.name}>
                                        {category.title}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            
            {productsLoading ? (
                <LoadingSpinner message="Загрузка списка товаров..." containerHeight="vh-50"/>
            ) : productsError ? (
                <div className="alert alert-danger">{productsError}</div>
            ) : (
                <>
                    {/* Информация о количестве товаров перед таблицей */}
                    {!productsLoading && totalProducts !== 0 &&
                        <div className="products-info text-muted mb-3">
                            Показано {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalProducts)} из {totalProducts} товаров
                        </div>
                    }

                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead className="bg-light">
                                <tr>
                                    {/* <th style={{width: "70px"}}>ID</th> */}
                                    <th style={{width: "100px"}}>Фото</th>
                                    <th>Название</th>
                                    <th>Категория</th>
                                    <th>Цена</th>
                                    <th style={{width: "150px"}}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products && products.length > 0 ? (
                                    products.map(product => (
                                        <tr key={product.id}>
                                            {/* <td>{product.id}</td> */}
                                            <td>
                                                <img 
                                                    src={product.image || defaultProductImage} 
                                                    alt={product.title}
                                                    style={{width: "60px", height: "60px", objectFit: "contain"}}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultProductImage;
                                                    }}
                                                />
                                            </td>
                                            <td>{product.title}</td>
                                            <td>
                                                {categories && categories.find(cat => cat.name === product.category)?.title || product.category}
                                            </td>
                                            <td>{formatPrice(product.price)}</td>
                                            <td>
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleEditProduct(product.id)}
                                                >
                                                    <FaPencilAlt />
                                                </Button>
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleDuplicateProduct(product.id)}
                                                >
                                                    <FaCopy />
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={() => openDeleteModal(product)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            Товары не найдены
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-2 mt-md-0">
                            <Pagination
                                currentPage={currentPage || currentPageState}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Модальное окно подтверждения удаления */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Подтверждение удаления</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {productToDelete && (
                        <p>Вы действительно хотите удалить товар "{productToDelete.title}"?</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDeleteProduct}>
                        Удалить
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminProducts;